import { useMemo } from 'react';
import { ChannelType } from 'src/api/generated';
import { isSessionActive } from 'src/modules/shared/utils/expiration.util';

import type {
  UseSessionMatchingParams,
  UseSessionMatchingReturn,
} from '../../../../types/whatsapp-address-selection.types';

/**
 * Hook that finds the corresponding channel session for selected addresses.
 * It will try to find a session that matches both the contact and funnel addresses.
 */
export const useSessionMatching = ({
  selectedContactAddress,
  selectedFunnelAddress,
  channelSessions,
}: UseSessionMatchingParams): UseSessionMatchingReturn => {
  const correspondingSession = useMemo(() => {
    if (!selectedContactAddress || !selectedFunnelAddress) return null;

    const exactMatch = channelSessions.find(
      (session) =>
        session.channelType === ChannelType.WHATSAPP &&
        session.contactAddressId === selectedContactAddress.id &&
        session.funnelAddressId === selectedFunnelAddress.id
    );

    return exactMatch ?? null;
  }, [channelSessions, selectedContactAddress, selectedFunnelAddress]);

  const sessionActive = useMemo(() => {
    if (!correspondingSession) return false;

    return isSessionActive(correspondingSession.endTime);
  }, [correspondingSession]);

  return {
    correspondingSession,
    isSessionActive: sessionActive,
  };
};
